import { Context, use } from "react";

export function useLazyContext<T>(context: LazyContext<T>): T {
  const value = use(context);

  if (value === undefined) return use(pending);

  return isThunk(value) ? use(value()) : value;
}

// Caches the promise so the load runs once and `use` sees the same promise on
// every render — a thunk returning a fresh promise would suspend forever.
export function lazyValue<T>(load: () => Promise<T>): () => Promise<T> {
  let promise: Promise<T> | undefined;
  return () => (promise ??= load());
}

export type LazyContext<T> = Context<LazyValue<T>>;

// A thunk rather than a promise, so an adapter chunk starts loading when a
// consumer first reads the port, not when the app boots. Detected with
// `typeof`, so T itself must not be a function type — ports are object bags.
export type LazyValue<T> = T | Thunk<T> | undefined;

export type Thunk<T> = () => Promise<T>;

function isThunk<T>(value: T | Thunk<T>): value is Thunk<T> {
  return typeof value === "function";
}

// An unprovided port suspends rather than throwing, so a provider can be introduced
// anywhere above the consumer without the consumers that read none of its ports
// having to wait. Never settles: the consumer is re-rendered when the provider
// mounts, not when this resolves.
const pending = new Promise<never>(() => undefined);
